import { Alert, AlertTitle, Box, Button, Checkbox, CircularProgress, FormControlLabel, FormGroup, FormHelperText, FormLabel, MenuItem, Select, Tab, Tabs, TextField } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { createCompany, CreateCompanyPayload, documents, DocumentTypes, getCompanyById, getCompanyEezyoneEnabled, setCompanyEezyoneEnabled, setCompanyHiddenStatus, subscriptions, SubscriptionTypes, updateCompany, updateMensalidadeQuitada } from "src/apis/company";
import FormSaveAndResetButtons from "src/pages/components/FormSaveAndResetButtons";
import PageContainer from "src/pages/components/PageContainer";
import { isUserAdministrator } from "src/utils/auth";
import { isSuccessOrAllRequiredFieldsAreFilled } from "src/utils/form";
import TrainingChecklistTab from "./components/TrainingChecklistTab";
import ComplianceIndicatorsTab from "./components/ComplianceIndicatorsTab";
import BlockSettingsTab from "./components/BlockSettingsTab";
import EezyGuardTab from "./components/EezyGuardTab";

const initialValues: CreateCompanyPayload = {
  trade_name: '',
  social_reason: '',
  subscription: 'PLANO',
  responsible: '',
  document: '',
  document_type: 'CNPJ',
  service_value: 0,
  monthly_value: 0,
  paid_services: 0,
  coparticipations: 0,
  coparticipation_value: 0,
}

const optionalFields: string[] = [
  'service_value',
  'monthly_value',
  'paid_services',
  'coparticipations',
  'coparticipation_value',
]

const RegisterCompany: FC = (): JSX.Element => {
  const [error, setError] = useState<string>('');
  const [warning, setWarning] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [hiddenLoading, setHiddenLoading] = useState<boolean>(false);
  const [eezyoneEnabled, setEezyoneEnabled] = useState<boolean>(true);
  const [eezyoneLoading, setEezyoneLoading] = useState<boolean>(false);
  const [isCompanyHidden, setIsCompanyHidden] = useState<boolean>(false);
  const [mensalidadeQuitada, setMensalidadeQuitada] = useState<boolean>(false);
  const [mensalidadeLoading, setMensalidadeLoading] = useState<boolean>(false);
  const [formValues, setFormValues] = useState<CreateCompanyPayload>(initialValues);
  const { id } = useParams();
  const [selectedTab, setSelectedTab] = useState<"general" | "checklist" | "indicators" | "blocks" | "eezyguard">("general");

  const isSubmitDisabled = useMemo(() => isSuccessOrAllRequiredFieldsAreFilled(success, formValues, optionalFields), [success, formValues]);

  const resetFormState = (): void => {
    setError('');
    setWarning('');
    setSuccess(false);
    setFormValues(initialValues);
    setIsCompanyHidden(false);
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setLoading(true);

    try {
      if (Boolean(id)) {
        await updateCompany(id, formValues);
      } else {
        await createCompany(formValues);
      }

      setError('');
      setWarning('');
      setSuccess(true);
    } catch (err: any) {
      const documentAlreadyTaken = err?.response?.data?.error?.includes("already exists");
      if (documentAlreadyTaken) {
        setWarning(`O documento ${formValues?.document} (${formValues?.document_type}) já está sendo utilizado! Por favor tente novamente com outro documento`)
      } else {
        setError(err?.message);
      }
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  }

  const fetchCompany = async (companyId: number|string|undefined): Promise<void> => {
    if (!companyId) return;

    try {
      const res = await getCompanyById(companyId);

      setFormValues({
        document: res.document,
        document_type: res.document_type,
        responsible: res.responsible,
        social_reason: res.social_reason,
        subscription: res.subscription,
        trade_name: res.trade_name,
        service_value: res.valor ?? 0.0,
        monthly_value: res.mensalidade ?? 0.0,
        paid_services: res?.consultas_pagas ?? 0,
        coparticipations: res?.coparticipativas ?? 0,
        coparticipation_value: res?.valor_cop ?? 0.0,
      });
      setIsCompanyHidden(Boolean(res?.oculto));
      setMensalidadeQuitada(Boolean(res?.mensalidade_quitada));
      try {
        setEezyoneEnabled(await getCompanyEezyoneEnabled(companyId));
      } catch {
        setEezyoneEnabled(true);
      }
    } catch (err: any) {
      setError(err?.message);
      setSuccess(false);
    }
  }

  const toggleEezyoneEnabled = async (): Promise<void> => {
    if (!id || eezyoneLoading) return;
    const next = !eezyoneEnabled;
    try {
      setEezyoneLoading(true);
      await setCompanyEezyoneEnabled(id, next);
      setEezyoneEnabled(next);
      setError('');
    } catch (err: any) {
      setError(err?.message ?? 'Erro ao atualizar o EezyOne da empresa.');
    } finally {
      setEezyoneLoading(false);
    }
  };

  const handleMensalidadeQuitadaChange = async (checked: boolean): Promise<void> => {
    if (!id || mensalidadeLoading) return;
    setMensalidadeLoading(true);
    try {
      await updateMensalidadeQuitada(id, checked);
      setMensalidadeQuitada(checked);
    } catch (err: any) {
      setError(err?.message ?? "Erro ao atualizar quitação da mensalidade.");
    } finally {
      setMensalidadeLoading(false);
    }
  };

  const toggleCompanyHiddenStatus = async (): Promise<void> => {
    if (!id || hiddenLoading) return;

    const nextHiddenStatus = !isCompanyHidden;

    try {
      setHiddenLoading(true);
      await setCompanyHiddenStatus(id, nextHiddenStatus);
      setIsCompanyHidden(nextHiddenStatus);
      setError('');
    } catch (err: any) {
      setError(err?.message ?? 'Erro ao atualizar visibilidade da empresa.');
    } finally {
      setHiddenLoading(false);
    }
  }

  useEffect(() => {
    if (!id) return;

    fetchCompany(id);
    setSelectedTab("general");
  }, [id])

  const canManageUpsells = useMemo(() => Boolean(id), [id]);

  const companyForm = (
      <form
        onSubmit={handleSubmit}
        style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
      >
        <FormGroup>
          <FormLabel>Nome Fantasia</FormLabel>
          <TextField
            type="text"
            value={formValues.trade_name}
            onChange={(e) => {
              setFormValues(previousValues => ({
                ...previousValues,
                trade_name: e.target.value as string,
              }));
            }}
          />
        </FormGroup>
        <FormGroup>
          <FormLabel>Razão Social</FormLabel>
          <TextField
            type="text"
            value={formValues.social_reason}
            onChange={(e) => {
              setFormValues(previousValues => ({
                ...previousValues,
                social_reason: e.target.value as string,
              }));
            }}
          />
        </FormGroup>
        <FormGroup>
          <FormLabel>Responsável Legal</FormLabel>
          <TextField
            type="text"
            value={formValues.responsible}
            onChange={(e) => {
              setFormValues(previousValues => ({
                ...previousValues,
                responsible: e.target.value as string,
              }));
            }}
          />
        </FormGroup>
        <FormGroup>
          <FormLabel>Tipo de documento</FormLabel>
          <Select
            labelId="type"
            name="type"
            onChange={(e) => {
              setFormValues(previousValues => ({
                ...previousValues,
                document_type: e.target.value as DocumentTypes,
              }));
              setSuccess(false);
            }}
            variant="outlined"
            value={formValues?.document_type ?? ''}
          >
            {Object.entries(documents).map(([key, value]) => (
              <MenuItem key={key} value={String(key)}>
                {value}
              </MenuItem>
            ))}
          </Select>
        </FormGroup>
        <FormGroup>
          <FormLabel>Número de Documento</FormLabel>
          <TextField
            type="text"
            value={formValues.document}
            onChange={(e) => {
              setFormValues(previousValues => ({
                ...previousValues,
                document: e.target.value as string,
              }));
            }}
          />
        </FormGroup>
        <FormGroup>
          <FormLabel>Tipo de Assinatura</FormLabel>
          <Select
            labelId="type"
            name="type"
            onChange={(e) => {
              setFormValues(previousValues => ({
                ...previousValues,
                subscription: e.target.value as SubscriptionTypes,
              }));
              setSuccess(false);
            }}
            variant="outlined"
            value={formValues?.subscription ?? ''}
          >
            {Object.entries(subscriptions).map(([key, value]) => (
              <MenuItem key={key} value={String(key)}>
                {value}
              </MenuItem>
            ))}
          </Select>
        </FormGroup>

        <FormGroup>
          <FormLabel>Valor por atendimento (R$)</FormLabel>
          <TextField
            type="number"
            value={formValues.service_value}
            onChange={(e) => {
              setFormValues(previousValues => ({
                ...previousValues,
                service_value: parseFloat(e.target.value),
              }));
            }}
          />
          <FormHelperText>
          Valor da por atendimento em reais, caso não se aplique manter como 0.
          </FormHelperText>
        </FormGroup>

        <FormGroup>
          <FormLabel>Mensalidade (R$)</FormLabel>
          <TextField
            type="number"
            value={formValues.monthly_value}
            onChange={(e) => {
              setFormValues(previousValues => ({
                ...previousValues,
                monthly_value: parseFloat(e.target.value),
              }));
            }}
          />
          <FormHelperText>
            Valor da mensalidade em reais, caso não se aplique manter como 0.
          </FormHelperText>
        </FormGroup>

        {Boolean(id) && (formValues.monthly_value ?? 0) > 0 && (
          <FormGroup>
            <FormControlLabel
              control={
                mensalidadeLoading
                  ? <CircularProgress size={20} sx={{ mx: 1.5 }} />
                  : <Checkbox
                      checked={mensalidadeQuitada}
                      onChange={(e) => handleMensalidadeQuitadaChange(e.target.checked)}
                      color="success"
                    />
              }
              label="Mensalidade do mês quitada?"
            />
            <FormHelperText>
              {mensalidadeQuitada
                ? "✓ Mensalidade quitada este mês. Será zerada automaticamente no dia 01."
                : "Marque quando o pagamento deste mês for confirmado."}
            </FormHelperText>
          </FormGroup>
        )}

        <FormGroup>
          <FormLabel>Consultas pagas</FormLabel>
          <TextField
            type="number"
            value={formValues.paid_services}
            onChange={(e) => {
              setFormValues(previousValues => ({
                ...previousValues,
                paid_services: parseInt(e.target.value),
              }));
            }}
          />
          <FormHelperText>
            Quantidade de consultas pagas pela empresa por mês, caso não se aplique manter como 0.
          </FormHelperText>
        </FormGroup>

        <FormGroup>
          <FormLabel>Coparticipações</FormLabel>
          <TextField
            type="number"
            value={formValues.coparticipations}
            onChange={(e) => {
              setFormValues(previousValues => ({
                ...previousValues,
                coparticipations: parseInt(e.target.value),
              }));
            }}
          />
          <FormHelperText>
            Coparticipações pagas pela empresa por mês, caso não se aplique manter como 0.
          </FormHelperText>
        </FormGroup>

        {Boolean(formValues?.coparticipations) && (
          <FormGroup>
            <FormLabel>Valor coparticipação (R$)</FormLabel>
            <TextField
              type="number"
              value={formValues.coparticipation_value}
              onChange={(e) => {
                setFormValues(previousValues => ({
                  ...previousValues,
                  coparticipation_value: parseFloat(e.target.value),
                }));
              }}
            />
            <FormHelperText>
              Valor da coparticipação em reais, caso não se aplique manter como 0.
            </FormHelperText>
          </FormGroup>
        )}

        <FormSaveAndResetButtons
          success={success}
          loading={loading}
          isSubmitDisabled={isSubmitDisabled}
          resetFormState={resetFormState}
          isResetFormStateDisabled={Boolean(id)}
        />
      </form>
  );

  return (
    <PageContainer title={Boolean(id) ? "Editando empresa" : "Adicionar empresa"} error={error} guard={isUserAdministrator}>
      {Boolean(warning) && (
        <Alert severity="warning">
          <AlertTitle>
            {warning}
          </AlertTitle>
        </Alert>
      )}

      {Boolean(id) && (
        <Box display="flex" justifyContent="flex-end" gap={2} mb={2} flexWrap="wrap">
          <Button
            variant="contained"
            color={eezyoneEnabled ? "warning" : "success"}
            onClick={toggleEezyoneEnabled}
            disabled={eezyoneLoading}
          >
            {eezyoneLoading
              ? 'Atualizando...'
              : eezyoneEnabled
                ? 'Desligar EezyOne'
                : 'Ligar EezyOne'}
          </Button>
          <Button
            variant="contained"
            color={isCompanyHidden ? "success" : "warning"}
            onClick={toggleCompanyHiddenStatus}
            disabled={hiddenLoading}
          >
            {hiddenLoading
              ? 'Atualizando...'
              : isCompanyHidden
                ? 'Desmarcar empresa como oculta'
                : 'Ocultar empresa'}
          </Button>
        </Box>
      )}

      {canManageUpsells ? (
        <Box display="flex" flexDirection="column" gap={3}>
          <Tabs
            value={selectedTab}
            onChange={(_, value) => setSelectedTab(value)}
            variant="scrollable"
            allowScrollButtonsMobile
          >
            <Tab label="Dados da empresa" value="general" />
            <Tab label="Checklist anual" value="checklist" />
            <Tab label="Indicadores" value="indicators" />
            <Tab label="Bloqueios" value="blocks" />
            <Tab label="EezyLine" value="eezyguard" />
          </Tabs>

          {selectedTab === "general" && companyForm}
          {selectedTab === "checklist" && <TrainingChecklistTab companyId={id ?? ''} />}
          {selectedTab === "indicators" && <ComplianceIndicatorsTab companyId={id ?? ''} />}
          {selectedTab === "blocks" && <BlockSettingsTab companyId={id ?? ''} />}
          {selectedTab === "eezyguard" && <EezyGuardTab companyId={id ?? ''} />}
        </Box>
      ) : (
        companyForm
      )}
    </PageContainer>
  );
}

export default RegisterCompany;
